import React, { useEffect, useState } from "react";
import {
    Dialog,
    useMediaQuery,
    useTheme,
    Typography,
    Box,
    Button,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    CircularProgress,
} from "@mui/material";
import { IoCloseCircleOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { getAllStudents, openBulkCoursesDialog } from "../../Redux/Dashboard/DashboardSlice";
import Spinner from "../../Spinner/Spinner";
import { Notify } from "../../CommonPopup/Popup";
import { getAllCoursesDetail, hideCoursesForBulkStudent } from "../../Redux/Auth/AuthSlice";
import {
    ExpandMore,
    Search,
    ChevronLeft,
    ChevronRight,
} from "@mui/icons-material";
import "../../Style/HideCourses.css";
import { useNavigate } from "react-router-dom";

const BulkCoursesDialog = () => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const loader = useSelector((s) => s.DashboardSlice.bulkActionLoader);
    const open = useSelector((s) => s.DashboardSlice.showBulkCoursesDialog?.open);
    const selectedBulkStudents = useSelector((s) => s.DashboardSlice.showBulkCoursesDialog?.users);
    const selectedBulkAction = useSelector((s) => s.DashboardSlice.showBulkCoursesDialog?.action);
    const allCourses = useSelector((s) => s.AuthSlice.getAllBulkCoursesDetails || []);
    const coursesLoader = useSelector((s) => s.AuthSlice.allCoursesLoader);

    const [activeTab, setActiveTab] = useState(0);
    const [expandedCourses, setExpandedCourses] = useState({});
    const [expandedLessons, setExpandedLessons] = useState({});
    const [expandedSubjects, setExpandedSubjects] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCourse, setSelectedCourse] = useState("");
    const [selectedLesson, setSelectedLesson] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("");
    const [checkedCourses, setCheckedCourses] = useState({});
    const [checkedLessons, setCheckedLessons] = useState({});
    const [checkedSubjects, setCheckedSubjects] = useState({});
    const [checkedVideos, setCheckedVideos] = useState({});
    const [filteredCourses, setFilteredCourses] = useState([]);

    const handleClose = () => {
        dispatch(openBulkCoursesDialog(false));
    };

    const handleSubmit = () => {
        const userIds = selectedBulkStudents
            .map((u) => u._id || u.id)
            .filter(Boolean);

        if (!userIds.length) {
            Notify({
                data: "error",
                msg: "No valid users selected",
                type: "error",
            });
            return;
        }

        const { selectedCourses, selectedLessons, selectedSubjects, selectedVideos } = getSelectedItems();

        const payload = {
            action: selectedBulkAction,
            userIds,
            courses: selectedCourses,
            lessons: selectedLessons,
            subjects: selectedSubjects,
            videos: selectedVideos,
        };

        dispatch(hideCoursesForBulkStudent(payload))
            .unwrap()
            .then((data) => {
                Notify({
                    data: data.success ? "success" : "error",
                    msg: data?.message,
                });

                if (data.success) {
                    // dispatch(getAllStudents());
                    dispatch(openBulkCoursesDialog(false));
                    navigate("/studentsList");
                }
            })
            .catch((err) => {
                Notify({
                    data: "error",
                    msg: err?.message || "Failed to perform action",
                });
            });
    };
    
    const actionLabels = {
        hide: "Mark as hide",
        unhide: "Mark as unhide",
    };

    useEffect(() => {
        dispatch(getAllCoursesDetail());
    }, [dispatch]);

    // Filter courses based on search and selections
    useEffect(() => {
        let filtered = allCourses.filter((course) => course.is_paid === true);

        if (searchQuery.trim()) {
            filtered = filtered
                .map((course) => {
                    const filteredLessons = course.lessons
                        .map((lesson) => {
                            const filteredSubjects = lesson.subjects
                                .map((subject) => {
                                    const filteredVideos = subject.videos.filter(
                                        (video) =>
                                            checkedVideos[video._id] &&
                                            video.name.toLowerCase().includes(searchQuery.toLowerCase())
                                    );
                                    return filteredVideos.length > 0 && checkedSubjects[subject.subjectId]
                                        ? { ...subject, videos: filteredVideos }
                                        : null;
                                })
                                .filter((subject) => subject !== null);
                            return filteredSubjects.length > 0 && checkedLessons[lesson.lessonId]
                                ? { ...lesson, subjects: filteredSubjects }
                                : null;
                        })
                        .filter((lesson) => lesson !== null);
                    return filteredLessons.length > 0 && checkedCourses[course.courseId]
                        ? { ...course, lessons: filteredLessons }
                        : null;
                })
                .filter((course) => course !== null);
        }

        if (selectedCourse) {
            filtered = filtered.filter((course) => course.courseId === selectedCourse);
            if (selectedLesson) {
                filtered = filtered.map((course) => ({
                    ...course,
                    lessons: course.lessons.filter((lesson) => lesson.lessonId === selectedLesson),
                }));
                if (selectedSubject) {
                    filtered = filtered.map((course) => ({
                        ...course,
                        lessons: course.lessons.map((lesson) => ({
                            ...lesson,
                            subjects: lesson.subjects.filter(
                                (subject) => subject.subjectId === selectedSubject
                            ),
                        })),
                    }));
                }
            }
        }

        setFilteredCourses(filtered.length > 0 ? filtered : []);
    }, [searchQuery, allCourses, selectedCourse, selectedLesson, selectedSubject, checkedCourses, checkedLessons, checkedSubjects, checkedVideos]);

    // Initialize checkbox states for all courses - all unchecked by default
    useEffect(() => {
        const newCheckedCourses = {};
        const newCheckedLessons = {};
        const newCheckedSubjects = {};
        const newCheckedVideos = {};

        allCourses.forEach((course) => {
            if (course.is_paid) {
                newCheckedCourses[course.courseId] = false;
                course.lessons.forEach((lesson) => {
                    newCheckedLessons[lesson.lessonId] = false;
                    lesson.subjects.forEach((subject) => {
                        newCheckedSubjects[subject.subjectId] = false;
                        subject.videos.forEach((video) => {
                            newCheckedVideos[video._id] = false;
                        });
                    });
                });
            }
        });

        setCheckedCourses(newCheckedCourses);
        setCheckedLessons(newCheckedLessons);
        setCheckedSubjects(newCheckedSubjects);
        setCheckedVideos(newCheckedVideos);
    }, [allCourses]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleCheckboxChange = (type, id) => {
        if (type === "course") {
            setCheckedCourses((prev) => {
                const newChecked = { ...prev, [id]: !prev[id] };
                // If course is unchecked, uncheck all its children
                if (!newChecked[id]) {
                    const course = allCourses.find((c) => c.courseId === id);
                    if (course) {
                        course.lessons.forEach((lesson) => {
                            setCheckedLessons((prevLessons) => ({ ...prevLessons, [lesson.lessonId]: false }));
                            lesson.subjects.forEach((subject) => {
                                setCheckedSubjects((prevSubjects) => ({ ...prevSubjects, [subject.subjectId]: false }));
                                subject.videos.forEach((video) => {
                                    setCheckedVideos((prevVideos) => ({ ...prevVideos, [video._id]: false }));
                                });
                            });
                        });
                    }
                }
                return newChecked;
            });
        } else if (type === "lesson") {
            // Only allow lesson selection if parent course is selected
            const course = allCourses.find((c) => c.lessons.some((l) => l.lessonId === id));
            if (course && checkedCourses[course.courseId]) {
                setCheckedLessons((prev) => {
                    const newChecked = { ...prev, [id]: !prev[id] };
                    // If lesson is unchecked, uncheck all its children
                    if (!newChecked[id]) {
                        const lesson = course.lessons.find((l) => l.lessonId === id);
                        if (lesson) {
                            lesson.subjects.forEach((subject) => {
                                setCheckedSubjects((prevSubjects) => ({ ...prevSubjects, [subject.subjectId]: false }));
                                subject.videos.forEach((video) => {
                                    setCheckedVideos((prevVideos) => ({ ...prevVideos, [video._id]: false }));
                                });
                            });
                        }
                    }
                    return newChecked;
                });
            }
        } else if (type === "subject") {
            // Only allow subject selection if parent lesson is selected
            let parentLesson = null;
            let parentCourse = null;
            
            for (const course of allCourses) {
                const lesson = course.lessons.find((l) => l.subjects.some((s) => s.subjectId === id));
                if (lesson) {
                    parentLesson = lesson;
                    parentCourse = course;
                    break;
                }
            }
            
            if (parentLesson && checkedCourses[parentCourse.courseId] && checkedLessons[parentLesson.lessonId]) {
                setCheckedSubjects((prev) => {
                    const newChecked = { ...prev, [id]: !prev[id] };
                    // If subject is unchecked, uncheck all its children
                    if (!newChecked[id]) {
                        const subject = parentLesson.subjects.find((s) => s.subjectId === id);
                        if (subject) {
                            subject.videos.forEach((video) => {
                                setCheckedVideos((prevVideos) => ({ ...prevVideos, [video._id]: false }));
                            });
                        }
                    }
                    return newChecked;
                });
            }
        } else if (type === "video") {
            // Only allow video selection if parent subject is selected
            let parentSubject = null;
            let parentLesson = null;
            let parentCourse = null;
            
            for (const course of allCourses) {
                for (const lesson of course.lessons) {
                    const subject = lesson.subjects.find((s) => s.videos.some((v) => v._id === id));
                    if (subject) {
                        parentSubject = subject;
                        parentLesson = lesson;
                        parentCourse = course;
                        break;
                    }
                }
                if (parentSubject) break;
            }
            
            if (parentSubject && checkedCourses[parentCourse.courseId] && 
                checkedLessons[parentLesson.lessonId] && checkedSubjects[parentSubject.subjectId]) {
                setCheckedVideos((prev) => ({ ...prev, [id]: !prev[id] }));
            }
        }
    };

    const toggleExpand = (type, id) => {
        if (type === "course") {
            setExpandedCourses((prev) => ({ ...prev, [id]: !prev[id] }));
        } else if (type === "lesson") {
            // Only allow lesson expansion if parent course is selected
            const course = allCourses.find((c) => c.lessons.some((l) => l.lessonId === id));
            if (course && checkedCourses[course.courseId]) {
                setExpandedLessons((prev) => ({ ...prev, [id]: !prev[id] }));
            }
        } else if (type === "subject") {
            // Only allow subject expansion if parent lesson is selected
            let parentLesson = null;
            let parentCourse = null;
            
            for (const course of allCourses) {
                const lesson = course.lessons.find((l) => l.subjects.some((s) => s.subjectId === id));
                if (lesson) {
                    parentLesson = lesson;
                    parentCourse = course;
                    break;
                }
            }
            
            if (parentLesson && checkedCourses[parentCourse.courseId] && checkedLessons[parentLesson.lessonId]) {
                setExpandedSubjects((prev) => ({ ...prev, [id]: !prev[id] }));
            }
        }
    };

    const handleDropdownChange = (type, value) => {
        if (type === "course") {
            setSelectedCourse(value);
            setSelectedLesson("");
            setSelectedSubject("");
        } else if (type === "lesson") {
            setSelectedLesson(value);
            setSelectedSubject("");
        } else if (type === "subject") {
            setSelectedSubject(value);
        }
    };

    const getSelectedItems = () => {
        const selectedCourses = Object.keys(checkedCourses).filter(id => checkedCourses[id]);
        const selectedLessons = Object.keys(checkedLessons).filter(id => checkedLessons[id]);
        const selectedSubjects = Object.keys(checkedSubjects).filter(id => checkedSubjects[id]);
        const selectedVideos = Object.keys(checkedVideos).filter(id => checkedVideos[id]);
        
        return { selectedCourses, selectedLessons, selectedSubjects, selectedVideos };
    };

    const renderCourseItem = (course, level = 1) => {
        const courseId = course.courseId;
        const isExpanded = expandedCourses[courseId];
        const isChecked = checkedCourses[courseId];

        return (
            <Accordion
                key={courseId}
                expanded={isExpanded}
                onChange={() => toggleExpand("course", courseId)}
                sx={{ mb: 1 }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                        bgcolor: isChecked ? "background.paper" : "action.disabled",
                        "& .MuiAccordionSummary-content": {
                            alignItems: "center",
                        },
                    }}
                >
                    <Box display="flex" alignItems="center" width="100%">
                        <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange("course", courseId);
                            }}
                            sx={{ mr: 2 }}
                        />
                        <Typography variant="subtitle1" fontWeight="600">
                            {course.courseTitle}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pl: 6 }}>
                    {course.lessons.map((lesson) => renderLessonItem(lesson, level + 1))}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderLessonItem = (lesson, level) => {
        const lessonId = lesson.lessonId;
        const isExpanded = expandedLessons[lessonId];
        const isChecked = checkedLessons[lessonId];
        
        // Find parent course to check if it's selected
        const parentCourse = allCourses.find((c) => c.lessons.some((l) => l.lessonId === lessonId));
        const isParentSelected = parentCourse && checkedCourses[parentCourse.courseId];

        return (
            <Accordion
                key={lessonId}
                expanded={isExpanded}
                onChange={() => toggleExpand("lesson", lessonId)}
                sx={{ mb: 1, ml: level > 1 ? 2 : 0 }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                        bgcolor: isChecked ? "background.paper" : (isParentSelected ? "action.disabled" : "action.disabledBackground"),
                        opacity: isParentSelected ? 1 : 0.5,
                    }}
                >
                    <Box display="flex" alignItems="center" width="100%">
                        <Checkbox
                            checked={isChecked}
                            disabled={!isParentSelected}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange("lesson", lessonId);
                            }}
                            sx={{ mr: 2 }}
                        />
                        <Typography variant="body2" fontWeight="500">
                            {lesson.lessonName}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pl: 6 }}>
                    {lesson.subjects.map((subject) => renderSubjectItem(subject, level + 1))}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderSubjectItem = (subject, level) => {
        const subjectId = subject.subjectId;
        const isExpanded = expandedSubjects[subjectId];
        const isChecked = checkedSubjects[subjectId];
        
        // Find parent lesson and course to check if they're selected
        let parentLesson = null;
        let parentCourse = null;
        
        for (const course of allCourses) {
            const lesson = course.lessons.find((l) => l.subjects.some((s) => s.subjectId === subjectId));
            if (lesson) {
                parentLesson = lesson;
                parentCourse = course;
                break;
            }
        }
        
        const isParentSelected = parentCourse && parentLesson && 
            checkedCourses[parentCourse.courseId] && checkedLessons[parentLesson.lessonId];

        return (
            <Accordion
                key={subjectId}
                expanded={isExpanded}
                onChange={() => toggleExpand("subject", subjectId)}
                sx={{ mb: 1, ml: level > 1 ? 2 : 0 }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                        bgcolor: isChecked ? "background.paper" : (isParentSelected ? "action.disabled" : "action.disabledBackground"),
                        opacity: isParentSelected ? 1 : 0.5,
                    }}
                >
                    <Box display="flex" alignItems="center" width="100%">
                        <Checkbox
                            checked={isChecked}
                            disabled={!isParentSelected}
                            onChange={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange("subject", subjectId);
                            }}
                            sx={{ mr: 2 }}
                        />
                        <Typography variant="body2" fontWeight="500">
                            {subject.subjectName}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pl: 6 }}>
                    {subject.videos.map((video) => renderVideoItem(video))}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderVideoItem = (video) => {
        const isChecked = checkedVideos[video._id];
        
        // Find parent subject, lesson, and course to check if they're selected
        let parentSubject = null;
        let parentLesson = null;
        let parentCourse = null;
        
        for (const course of allCourses) {
            for (const lesson of course.lessons) {
                const subject = lesson.subjects.find((s) => s.videos.some((v) => v._id === video._id));
                if (subject) {
                    parentSubject = subject;
                    parentLesson = lesson;
                    parentCourse = course;
                    break;
                }
            }
            if (parentSubject) break;
        }
        
        const isParentSelected = parentCourse && parentLesson && parentSubject &&
            checkedCourses[parentCourse.courseId] && 
            checkedLessons[parentLesson.lessonId] && 
            checkedSubjects[parentSubject.subjectId];

        return (
            <Box
                key={video._id}
                display="flex"
                alignItems="center"
                sx={{ py: 1, ml: 2 }}
            >
                <Checkbox
                    checked={isChecked}
                    disabled={!isParentSelected}
                    onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("video", video._id);
                    }}
                    sx={{ mr: 2 }}
                />
                <Typography variant="body2" sx={{ opacity: isParentSelected ? 1 : 0.5 }}>
                    {video.name}
                </Typography>
            </Box>
        );
    };

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 3,
                    bgcolor: "#fafafa",
                    minHeight: "600px",
                },
            }}
        >
            {coursesLoader ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h5" fontWeight="bold">
                            Confirm Action for courses
                        </Typography>
                        <IoCloseCircleOutline
                            size={28}
                            onClick={handleClose}
                            style={{ cursor: "pointer", color: "#f44336" }}
                        />
                    </Box>

                    {/* Action Info */}
                    <Typography mb={1} fontWeight="600">
                        Action:
                    </Typography>
                    <Typography mb={2} color="primary">
                        {actionLabels[selectedBulkAction] || selectedBulkAction}
                    </Typography>

                    {/* Tabs */}
                    <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                        <Tab label={`Selected Users (${selectedBulkStudents?.length || 0})`} />
                        <Tab label="Select Courses" />
                    </Tabs>

                    {/* Tab Content */}
                    {activeTab === 0 && (
                        <Box>
                            <Typography mb={2} fontWeight="600">
                                Selected Users ({selectedBulkStudents?.length || 0}):
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: "300px",
                                    overflowY: "auto",
                                    background: "#f1f5f9",
                                    borderRadius: "8px",
                                    p: 2,
                                }}
                            >
                                {selectedBulkStudents?.map((user) => (
                                    <Typography key={user?.id || user?._id} fontSize={14} sx={{ py: 0.5 }}>
                                        • {user?.name}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {activeTab === 1 && (
                        <Box>
                            {/* Search and Dropdown Controls */}
                            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                                <TextField
                                    size="small"
                                    placeholder="Search videos..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ minWidth: "250px" }}
                                />

                                <FormControl size="small" sx={{ minWidth: "200px" }}>
                                    <InputLabel>All Paid Courses</InputLabel>
                                    <Select
                                        value={selectedCourse}
                                        onChange={(e) => handleDropdownChange("course", e.target.value)}
                                        label="All Paid Courses"
                                    >
                                        <MenuItem value="">All Paid Courses</MenuItem>
                                        {allCourses
                                            .filter((course) => course.is_paid === true)
                                            .map((course) => (
                                                <MenuItem key={course.courseId} value={course.courseId}>
                                                    {course.courseTitle}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: "200px" }} disabled={!selectedCourse}>
                                    <InputLabel>All Lessons</InputLabel>
                                    <Select
                                        value={selectedLesson}
                                        onChange={(e) => handleDropdownChange("lesson", e.target.value)}
                                        label="All Lessons"
                                    >
                                        <MenuItem value="">All Lessons</MenuItem>
                                        {allCourses
                                            .find((c) => c.courseId === selectedCourse)
                                            ?.lessons.map((lesson) => (
                                                <MenuItem key={lesson.lessonId} value={lesson.lessonId}>
                                                    {lesson.lessonName}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: "200px" }} disabled={!selectedLesson}>
                                    <InputLabel>All Subjects</InputLabel>
                                    <Select
                                        value={selectedSubject}
                                        onChange={(e) => handleDropdownChange("subject", e.target.value)}
                                        label="All Subjects"
                                    >
                                        <MenuItem value="">All Subjects</MenuItem>
                                        {allCourses
                                            .find((c) => c.courseId === selectedCourse)
                                            ?.lessons.find((l) => l.lessonId === selectedLesson)
                                            ?.subjects.map((subject) => (
                                                <MenuItem key={subject.subjectId} value={subject.subjectId}>
                                                    {subject.subjectName}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Courses List */}
                            <Box
                                sx={{
                                    maxHeight: "400px",
                                    overflowY: "auto",
                                    background: "#f8f9fa",
                                    borderRadius: "8px",
                                    p: 2,
                                }}
                            >
                                {filteredCourses.length > 0 ? (
                                    filteredCourses.map((course) => renderCourseItem(course))
                                ) : (
                                    <Typography textAlign="center" color="text.secondary">
                                        {searchQuery || selectedCourse
                                            ? "No paid videos found matching your criteria"
                                            : "No paid courses available"}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Warning */}
                    {selectedBulkAction === "delete" && (
                        <Typography mt={2} color="error" fontSize={14}>
                            ⚠️ This action may be irreversible. Please confirm before proceeding.
                        </Typography>
                    )}

                    {/* Buttons */}
                    <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="outlined" onClick={handleClose}>
                            Cancel
                        </Button>

                        <Button
                            variant="contained"
                            color={selectedBulkAction === "delete" ? "error" : "primary"}
                            onClick={handleSubmit}
                            disabled={loader}
                        >
                            {loader ? <Spinner /> : "Confirm"}
                        </Button>
                    </Box>
                </Box>
            )}
        </Dialog>
    );
};

export default BulkCoursesDialog;
